import {
  EntlastungDetail,
  EntlastungSaveRequest,
  EntlastungStatus,
  EntlastungUebersicht,
  EntlastungsArtikel,
  Entlastungsgrund,
} from '../models/entlastung.model';
import { ArtikelEinheitDto, normalizeArtikelEinheiten } from '../../../shared/models/artikel-einheit.model';

export interface EntlastungOverviewDto {
  readonly filialNummer: number;
  readonly filialName: string;
  readonly datum: string;
  readonly status: EntlastungStatus;
  readonly positionen: number;
  readonly positionenOffen: number;
  readonly menge: number;
}

export interface EntlastungPositionDto {
  readonly id: string;
  readonly artikelnummer: number;
  readonly artikelName: string;
  readonly menge: number;
  readonly einheitId: number | null;
  readonly entlastungsgrundId: number | null;
  readonly status: EntlastungStatus;
}

export interface EntlastungDetailDto {
  readonly filialNummer: number;
  readonly datum: string;
  readonly positionen: readonly EntlastungPositionDto[];
}

export interface EntlastungsgrundDto {
  readonly id: number;
  readonly grund: string;
  readonly beschreibung: string | null;
}

export interface EntlastungsArtikelDto {
  readonly artikelnummer: number;
  readonly artikelName: string;
  readonly warengruppeId: number | null;
  readonly warengruppeName: string | null;
  readonly grundEinheitId: number;
  readonly gruende: readonly { readonly id: number; readonly grund: string }[];
  readonly einheiten: readonly ArtikelEinheitDto[];
}

export interface EntlastungSavePositionDto {
  readonly id: string | null;
  readonly artikelnummer: number;
  readonly entlastungsgrundId: number;
  readonly einheitId: number;
  readonly menge: number;
}

export class InvalidEntlastungPositionError extends Error {
  override readonly name = 'InvalidEntlastungPositionError';

  constructor(readonly field: string) {
    super(`Entlastung: ungültiges Feld ${field}`);
  }
}

export function mapEntlastungOverview(items: readonly EntlastungOverviewDto[]): EntlastungUebersicht[] {
  return items.map((item) => ({
    filialNummer: item.filialNummer,
    filialName: item.filialName,
    datum: item.datum,
    status: item.status,
    anzahlPositionen: Number.isFinite(item.positionen) ? item.positionen : 0,
    anzahlPositionenOffen: Number.isFinite(item.positionenOffen) ? item.positionenOffen : 0,
    gesamtMenge: Number.isFinite(item.menge) ? item.menge : 0,
  }));
}

export function mapEntlastungGruende(items: readonly EntlastungsgrundDto[]): Entlastungsgrund[] {
  return items.map((item, index) => ({
    id: String(item.id),
    name: item.grund,
    sortOrder: index,
  }));
}

export function mapEntlastungArtikel(items: readonly EntlastungsArtikelDto[]): EntlastungsArtikel[] {
  return items.map((item) => ({
    id: String(item.artikelnummer),
    artikelNummer: String(item.artikelnummer),
    artikelName: item.artikelName,
    warengruppe: item.warengruppeName ?? '',
    grundEinheitId: item.grundEinheitId,
    erlaubteGrundIds: item.gruende.map((grund) => String(grund.id)),
    erlaubteEinheiten: normalizeArtikelEinheiten(item.einheiten).map((einheit) => ({
      id: einheit.id,
      bezeichnung: einheit.name,
      menge: einheit.menge,
      baseEinheitId: einheit.baseEinheitId,
      nr: einheit.nr,
      isDefault: einheit.isDefault,
      faktorZurKleinstenEinheit: einheit.faktorZurKleinstenEinheit,
    })),
  }));
}

export function mapEntlastungDetail(
  item: EntlastungDetailDto,
  filialName: string,
  artikel: readonly EntlastungsArtikel[]
): EntlastungDetail {
  const artikelByNumber = new Map(artikel.map((entry) => [entry.artikelNummer, entry]));

  return {
    filialNummer: item.filialNummer,
    filialName,
    datum: item.datum,
    positionen: item.positionen.map((position) => {
      const artikelNummer = String(position.artikelnummer);
      const katalogArtikel = artikelByNumber.get(artikelNummer);

      return {
        id: position.id,
        artikelId: artikelNummer,
        artikelNummer,
        artikelName: position.artikelName,
        warengruppe: katalogArtikel?.warengruppe ?? '',
        // The current UI uses non-null IDs; invalid placeholders are rejected by mapEntlastungSave.
        grundId: position.entlastungsgrundId === null ? '' : String(position.entlastungsgrundId),
        einheitId: position.einheitId ?? 0,
        einheit: katalogArtikel?.erlaubteEinheiten.find(
          (unit) => unit.id === position.einheitId
        )?.bezeichnung ?? '',
        menge: position.menge,
      };
    }),
  };
}

export function mapEntlastungSave(request: EntlastungSaveRequest): EntlastungSavePositionDto[] {
  return request.positionen.map((position) => {
    const artikelnummer = Number(position.artikelId);
    const entlastungsgrundId = Number(position.grundId);

    if (!Number.isSafeInteger(artikelnummer) || artikelnummer <= 0) {
      throw new InvalidEntlastungPositionError('artikelId');
    }
    if (!position.grundId || !Number.isSafeInteger(entlastungsgrundId) || entlastungsgrundId < 0) {
      throw new InvalidEntlastungPositionError('grundId');
    }
    if (!Number.isSafeInteger(position.einheitId) || position.einheitId < 0) {
      throw new InvalidEntlastungPositionError('einheitId');
    }
    if (!Number.isFinite(position.menge) || position.menge < 0) {
      throw new InvalidEntlastungPositionError('menge');
    }

    return {
      id: position.id ?? null,
      artikelnummer,
      entlastungsgrundId,
      einheitId: position.einheitId,
      menge: position.menge,
    };
  });
}
